import { Baseline } from '@shared/models/project.model';
import { hasPartialChanges } from '../../object.helpers';
import { ProjectChangeType, VersionChange } from '../update-engine.models';

/**
 * Convert a version-scoped change to a partial version patch (for batch apply).
 *
 * Only handles primitive top-level replacements ({@link ProjectChangeType.VersionName},
 * {@link ProjectChangeType.VersionProducts}, {@link ProjectChangeType.VersionResults},
 * {@link ProjectChangeType.VersionImports}).
 *
 * Structural mutations of `proposals` / `proposals` / `actions` are NOT handled here —
 * a `Partial<Baseline>` for those collections would risk overwriting concurrent
 * writes inside the same batch (e.g. ActionRename + ActionDelete both producing a full
 * `actions` array). Those changes are dispatched via `mutators/version-mutate.mutator.ts`
 * which returns a full updated {@link Baseline}, see {@link ElementChangeKind}
 * `'mutate'`.
 *
 * @param version - Current version state (read-only)
 * @param change - Version-level project change
 * @returns Partial<Baseline> or undefined if no change
 */
export const versionChangeToPartial = (
  version: Baseline,
  change: VersionChange,
): Partial<Baseline> | undefined => {
  switch (change.type) {
    case ProjectChangeType.VersionProducts: {
      const partial = { products: change.products };
      if (!hasPartialChanges(version, partial)) return undefined;
      return partial;
    }
    case ProjectChangeType.VersionResults: {
      const resultsRecord = {
        ...version.resultsRecord,
        [version.id]: change.results,
      };
      const partial = { resultsRecord };
      if (!hasPartialChanges(version, partial)) return undefined;
      return partial;
    }
    case ProjectChangeType.VersionName: {
      if (version.name === change.name) return undefined;
      return { name: change.name };
    }
    case ProjectChangeType.VersionImports: {
      const partial = { imports: change.imports };
      if (!hasPartialChanges(version, partial)) return undefined;
      return partial;
    }
    default:
      return undefined;
  }
};
